import {
  computed,
  onMounted,
  watch,
  onBeforeUnmount,
  toValue,
  toRefs,
  nextTick
} from "vue";
import {
  useEventListener,
  unrefElement,
  useResizeObserver,
  useThrottleFn,
  useScrollLock
} from "@vueuse/core";
import {
  guardedReleasePointerCapture,
  guardedSetPointerCapture,
  isIOS,
  isWithinRange
} from "@maas/vue-equipment/utils";
import {
  useMagicEmitter
} from "@maas/vue-equipment/plugins";
import { useMagicDrawer } from "./../useMagicDrawer.mjs";
import { useDrawerSnap } from "./useDrawerSnap.mjs";
import { useDrawerGuards } from "./useDrawerGuards.mjs";
import { useDrawerUtils } from "./useDrawerUtils.mjs";
import { useDrawerState } from "./useDrawerState.mjs";
export function useDrawerDrag(args) {
  const {
    id,
    elRef,
    wrapperRef,
    position,
    snapPoints,
    overshoot,
    threshold,
    initial,
    animation,
    preventDragClose,
    disabled
  } = args;
  const { initializeState } = useDrawerState(toValue(id));
  const state = initializeState();
  const {
    dragStart,
    dragging,
    wheeling,
    shouldClose,
    interpolateTo,
    originX,
    originY,
    lastDraggedX,
    lastDraggedY,
    draggedX,
    draggedY,
    relDirectionX,
    relDirectionY,
    absDirectionX,
    absDirectionY,
    elRect,
    wrapperRect
  } = toRefs(state);
  const { isActive, close } = useMagicDrawer(id);
  let pointerdownTarget = void 0;
  let cancelPointerup = void 0;
  let cancelPointermove = void 0;
  let cancelTouchend = void 0;
  let scrollLock = void 0;
  const hasDragged = computed(() => {
    const hasDraggedX = !isWithinRange({
      input: draggedX.value,
      base: lastDraggedX.value,
      threshold: toValue(threshold).lock
    });
    const hasDraggedY = !isWithinRange({
      input: draggedY.value,
      base: lastDraggedY.value,
      threshold: toValue(threshold).lock
    });
    return hasDraggedX || hasDraggedY;
  });
  const style = computed(
    () => `transform: translate(${draggedX.value}px, ${draggedY.value}px)`
  );
  const {
    snappedY,
    snappedX,
    activeSnapPoint,
    snapTo,
    snapPointsMap,
    interpolateDragged,
    findClosestSnapPoint,
    drawerHeight,
    drawerWidth
  } = useDrawerSnap({
    id,
    wrapperRect,
    animation,
    snapPoints,
    preventDragClose,
    position,
    overshoot,
    draggedY,
    draggedX
  });
  const { canDrag, canInterpolate, lockScroll } = useDrawerGuards({
    elRef,
    absDirectionX,
    absDirectionY,
    position,
    activeSnapPoint
  });
  const { clamp } = useDrawerUtils();
  const emitter = useMagicEmitter();
  async function getSizes() {
    elRect.value = unrefElement(elRef)?.getBoundingClientRect();
    wrapperRect.value = unrefElement(wrapperRef)?.getBoundingClientRect();
    await nextTick();
  }
  function checkPosition() {
    const distanceX = Math.abs(draggedX.value - lastDraggedX.value);
    const distanceY = Math.abs(draggedY.value - lastDraggedY.value);
    switch (position) {
      case "bottom":
      case "top": {
        if (distanceY > toValue(threshold).distance) {
          const snapPointY = findClosestSnapPoint({
            draggedX: 0,
            draggedY,
            direction: relDirectionY.value
          });
          if (Math.abs(snapPointY ?? 0) === drawerHeight.value) {
            shouldClose.value = true;
          } else {
            interpolateTo.value = snapPointY;
          }
        }
        break;
      }
      case "right":
      case "left": {
        if (distanceX > toValue(threshold).distance) {
          const snapPointX = findClosestSnapPoint({
            draggedX,
            draggedY: 0,
            direction: relDirectionX.value
          });
          if (Math.abs(snapPointX ?? 0) === drawerWidth.value) {
            shouldClose.value = true;
          } else {
            interpolateTo.value = snapPointX;
          }
        }
        break;
      }
    }
  }
  function checkMomentum() {
    const elapsed = Date.now() - dragStart.value.getTime();
    const distanceX = Math.abs(draggedX.value - lastDraggedX.value);
    const distanceY = Math.abs(draggedY.value - lastDraggedY.value);
    const velocityX = elapsed && distanceX ? distanceX / elapsed : 0;
    const velocityY = elapsed && distanceY ? distanceY / elapsed : 0;
    switch (position) {
      case "bottom":
      case "top": {
        if (velocityY > toValue(threshold).momentum) {
          const snapPointY = findClosestSnapPoint({
            draggedX: 0,
            draggedY,
            direction: relDirectionY.value
          });
          if (Math.abs(snapPointY ?? 0) === drawerHeight.value) {
            shouldClose.value = true;
          } else {
            interpolateTo.value = snapPointY;
          }
        }
        break;
      }
      case "right":
      case "left": {
        if (velocityX > toValue(threshold).momentum) {
          const snapPointX = findClosestSnapPoint({
            draggedX,
            draggedY,
            direction: relDirectionX.value
          });
          if (Math.abs(snapPointX ?? 0) === drawerWidth.value) {
            shouldClose.value = true;
          } else {
            interpolateTo.value = snapPointX;
          }
        }
        break;
      }
    }
  }
  function setDragged({ x, y }) {
    switch (position) {
      case "bottom": {
        const newDraggedB = clamp(y - originY.value, 0, toValue(overshoot) * -1);
        if (newDraggedB === draggedY.value) break;
        relDirectionY.value = newDraggedB < draggedY.value ? "below" : "above";
        draggedY.value = newDraggedB;
        break;
      }
      case "top": {
        const newDraggedT = clamp(y - originY.value, 0, toValue(overshoot));
        if (newDraggedT === draggedY.value) break;
        relDirectionY.value = newDraggedT < draggedY.value ? "below" : "above";
        draggedY.value = newDraggedT;
        break;
      }
      case "right": {
        const newDraggedR = clamp(x - originX.value, 0, toValue(overshoot) * -1);
        if (newDraggedR === draggedX.value) break;
        relDirectionX.value = newDraggedR < draggedX.value ? "below" : "above";
        draggedX.value = newDraggedR;
        break;
      }
      case "left": {
        const newDraggedL = clamp(x - originX.value, 0, toValue(overshoot));
        if (newDraggedL === draggedX.value) break;
        relDirectionX.value = newDraggedL < draggedX.value ? "below" : "above";
        draggedX.value = newDraggedL;
        break;
      }
    }
  }
  function checkDirection({ x, y }) {
    switch (position) {
      case "bottom":
        absDirectionY.value = y < originY.value ? "with" : y > originY.value ? "against" : void 0;
        break;
      case "top":
        absDirectionY.value = y > originY.value ? "with" : y < originY.value ? "against" : void 0;
        break;
      case "right":
        absDirectionX.value = x < originX.value ? "with" : x > originX.value ? "against" : void 0;
        break;
      case "left":
        absDirectionX.value = x > originX.value ? "with" : x < originX.value ? "against" : void 0;
        break;
    }
  }
  function resetStateAndListeners() {
    dragging.value = false;
    shouldClose.value = false;
    interpolateTo.value = void 0;
    cancelTouchend?.();
    cancelPointerup?.();
    cancelPointermove?.();
  }
  function resetScrollLock() {
    if (scrollLock?.value) {
      scrollLock.value = false;
    }
    scrollLock = void 0;
  }
  function resetState() {
    draggedX.value = 0;
    draggedY.value = 0;
    lastDraggedX.value = 0;
    lastDraggedY.value = 0;
    originX.value = 0;
    originY.value = 0;
    elRect.value = void 0;
    wrapperRect.value = void 0;
  }
  function resetSnapped() {
    snappedX.value = 0;
    snappedY.value = 0;
    activeSnapPoint.value = void 0;
  }
  function afterLeaveCallback(payload) {
    if (payload === toValue(id)) {
      resetState();
      resetSnapped();
    }
  }
  function snapToCallback(payload) {
    if (payload.id === toValue(id)) {
      if (!toValue(isActive)) {
        console.warn("Cannot snap to point when drawer is not open");
        return;
      } else {
        snapTo({
          snapPoint: payload.snapPoint,
          interpolate: true,
          duration: payload.duration
        });
      }
    }
  }
  async function initialize() {
    await getSizes();
    emitter.on("snapTo", snapToCallback);
    emitter.on("afterLeave", afterLeaveCallback);
  }
  function destroy() {
    emitter.off("snapTo", snapToCallback);
    emitter.off("afterLeave", afterLeaveCallback);
  }
  function onCancel() {
    switch (position) {
      case "bottom":
      case "top":
        interpolateDragged({
          to: snappedY.value
        });
        break;
      case "right":
      case "left":
        interpolateDragged({
          to: snappedX.value
        });
        break;
    }
    resetStateAndListeners();
    resetScrollLock();
  }
  function onPointerup(e) {
    if (shouldClose.value) {
      close();
    } else if (interpolateTo.value || interpolateTo.value === 0) {
      if (scrollLock && scrollLock.value || canInterpolate(e.target)) {
        interpolateDragged({
          to: interpolateTo.value
        });
      }
      activeSnapPoint.value = snapPointsMap.value[interpolateTo.value];
      switch (position) {
        case "bottom":
        case "top":
          snappedY.value = interpolateTo.value;
          break;
        case "right":
        case "left":
          snappedX.value = interpolateTo.value;
          break;
      }
    } else {
      switch (position) {
        case "bottom":
        case "top":
          interpolateDragged({
            to: snappedY.value
          });
          break;
        case "right":
        case "left":
          interpolateDragged({
            to: snappedX.value
          });
          break;
      }
    }
    emitter.emit("afterDrag", {
      id: toValue(id),
      x: draggedX.value,
      y: draggedY.value
    });
    resetStateAndListeners();
    resetScrollLock();
    if (hasDragged.value) {
      e.preventDefault();
    }
    guardedReleasePointerCapture({ event: e, element: pointerdownTarget });
  }
  function onPointermove(e) {
    if (e.isTrusted && wheeling.value) {
      return;
    }
    if (e.isTrusted && !e.isPrimary) {
      return;
    }
    e.stopImmediatePropagation();
    e.stopPropagation();
    shouldClose.value = false;
    checkDirection({ x: e.screenX, y: e.screenY });
    if (!scrollLock) {
      const target = lockScroll(e.target);
      if (target) {
        scrollLock = useScrollLock(target);
        scrollLock.value = true;
      }
    }
    if (!canDrag(e.target)) {
      return;
    }
    setDragged({ x: e.screenX, y: e.screenY });
    checkMomentum();
    checkPosition();
    emitter.emit("drag", {
      id: toValue(id),
      x: draggedX.value,
      y: draggedY.value
    });
  }
  function onPointerdown(e) {
    if (dragging.value) {
      return;
    } else {
      dragging.value = true;
      pointerdownTarget = e.target;
      guardedSetPointerCapture({
        event: e,
        element: e.target
      });
      emitter.emit("beforeDrag", {
        id: toValue(id),
        x: draggedX.value,
        y: draggedY.value
      });
    }
    lastDraggedX.value = draggedX.value;
    lastDraggedY.value = draggedY.value;
    cancelPointerup = useEventListener(document, "pointerup", onPointerup);
    cancelPointermove = useEventListener(
      document,
      "pointermove",
      onPointermove,
      { passive: false }
    );
    cancelTouchend = isIOS() ? useEventListener(document, "touchend", onPointerup) : void 0;
    originX.value = e.screenX - draggedX.value;
    originY.value = e.screenY - draggedY.value;
    dragStart.value = /* @__PURE__ */ new Date();
    onPointermove(e);
  }
  function onClick(e) {
    if (hasDragged.value) {
      e.preventDefault();
    }
  }
  onMounted(async () => {
    await initialize();
  });
  watch(
    () => [unrefElement(elRef), unrefElement(wrapperRef)],
    async () => {
      await getSizes();
      const snapPoint = toValue(initial)?.snapPoint;
      if (snapPoint) {
        snapTo({ snapPoint, interpolate: false });
      }
    }
  );
  watch([snappedX, snappedY], ([x, y]) => {
    lastDraggedX.value = x;
    lastDraggedY.value = y;
  });
  watch(
    () => toValue(disabled),
    (value) => {
      if (value) {
        onCancel();
      }
    }
  );
  useResizeObserver(elRef, async () => {
    useThrottleFn(async () => {
      await getSizes();
      if (activeSnapPoint.value) {
        await snapTo({ snapPoint: activeSnapPoint.value, interpolate: false });
        snapPointsMap.trigger();
      }
    }, 100)();
  });
  onBeforeUnmount(() => {
    destroy();
  });
  return {
    onPointerdown,
    onClick,
    style,
    hasDragged
  };
}
